// ============================================================
//  usePermission - Role Based Access Control Hook
// ============================================================

import { getOptionalSelectedCompanyId } from "./utils/companyContext";

type TokenPayload = {
  permissions?: unknown;
  role?: unknown;
  access_role?: unknown;
  company_id?: unknown;
};

const PLATFORM_ROLES = new Set(["SUPER_ADMIN", "PLATFORM_ADMIN", "OWNER"]);

const usePermission = () => {
  const selectedCompanyId = getOptionalSelectedCompanyId();
  const scopedStoredRole = selectedCompanyId
    ? localStorage.getItem(`erp_company_role_${selectedCompanyId}`)
    : null;
  const scopedStoredRoleName = selectedCompanyId
    ? localStorage.getItem(`erp_company_role_name_${selectedCompanyId}`)
    : null;
  const storedRole = localStorage.getItem("userRole") || "";
  const storedRoleName = localStorage.getItem("roleName") || "";
  const rawPerms = localStorage.getItem("permissions");

  const decodeTokenPayload = (): TokenPayload => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return {};
      const parts = token.split(".");
      if (parts.length !== 3) return {};
      const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      const pad = b64.length % 4 ? "=".repeat(4 - (b64.length % 4)) : "";
      return JSON.parse(atob(b64 + pad));
    } catch {
      return {};
    }
  };

  const tokenPayload = decodeTokenPayload();
  const tokenCompanyId = Number(tokenPayload.company_id || 0);
  const tokenMatchesSelectedCompany = Boolean(
    selectedCompanyId && tokenCompanyId > 0 && tokenCompanyId === selectedCompanyId
  );

  const decodeTokenPermissions = (): string[] | null => {
    return Array.isArray(tokenPayload?.permissions)
      ? tokenPayload.permissions.filter((p: unknown) => typeof p === "string")
      : null;
  };

  const parseStoredPermissions = (): string[] => {
    const tokenPermissions = decodeTokenPermissions();
    // The signed token is authoritative for the selected company. Local
    // storage is only a compatibility fallback when no token claim exists.
    if (tokenPermissions !== null && tokenMatchesSelectedCompany) return tokenPermissions;
    if (selectedCompanyId) {
      const scopedRaw = localStorage.getItem(`erp_company_permissions_${selectedCompanyId}`);
      if (scopedRaw) {
        try {
          const scoped = JSON.parse(scopedRaw);
          return Array.isArray(scoped)
            ? scoped.filter((p: unknown) => typeof p === "string")
            : [];
        } catch {
          return [];
        }
      }
      // Login-company claims must never drive another company's UI. The API
      // client refreshes a company-scoped token; until then we fail closed.
      if (tokenCompanyId > 0 && tokenCompanyId !== selectedCompanyId) return [];
    }
    if (!rawPerms) return [];
    try {
      const parsed = JSON.parse(rawPerms);
      return Array.isArray(parsed)
        ? parsed.filter((p: unknown) => typeof p === "string")
        : [];
    } catch {
      return [];
    }
  };

  const parseEnabledModules = (): Set<string> | null => {
    try {
      const companyId = getOptionalSelectedCompanyId();
      const raw = companyId
        ? localStorage.getItem(`erp_enabled_modules_${companyId}`)
        : null;
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return null;
      return new Set(parsed.filter((item: unknown) => typeof item === "string"));
    } catch {
      return null;
    }
  };

  const permissions: string[] = parseStoredPermissions();
  const enabledModules = parseEnabledModules();
  const role = scopedStoredRole || (tokenMatchesSelectedCompany ? storedRole : "");
  const roleName = scopedStoredRoleName || (tokenMatchesSelectedCompany ? storedRoleName : "");
  const normalizedRole = String(role || "").toUpperCase();
  const normalizedTokenRole = tokenMatchesSelectedCompany && typeof tokenPayload.role === "string"
    ? tokenPayload.role.toUpperCase()
    : "";
  const scopedStoredAccessRole = selectedCompanyId
    ? localStorage.getItem(`erp_company_access_role_${selectedCompanyId}`)
    : null;
  const storedAccessRole = scopedStoredAccessRole || (
    tokenMatchesSelectedCompany
      ? (localStorage.getItem("company_access_role") || localStorage.getItem("erp_access_role") || "")
      : ""
  );
  const tokenAccessRole = tokenMatchesSelectedCompany && typeof tokenPayload.access_role === "string"
    ? tokenPayload.access_role
    : "";
  const normalizedAccessRole = String(storedAccessRole || tokenAccessRole || "").toUpperCase();
  const isPlatformAdmin = (
    PLATFORM_ROLES.has(normalizedRole)
    || PLATFORM_ROLES.has(normalizedTokenRole)
    || PLATFORM_ROLES.has(normalizedAccessRole)
  );
  const isAdmin = normalizedRole === "ADMIN" || permissions.includes("*:*") || isPlatformAdmin;

  const isManager = normalizedRole === "MANAGER" || isAdmin;
  const isViewer = normalizedRole === "VIEWER" && !isAdmin;

  const can = (module: string, action: string): boolean => {
    if (enabledModules && !enabledModules.has(module)) return false;
    return permissions.includes("*:*") || permissions.includes(`${module}:${action}`);
  };

  const canView = (module: string) => can(module, "view");
  const canCreate = (module: string) => can(module, "create");
  const canEdit = (module: string) => can(module, "edit");
  const canDelete = (module: string) => can(module, "delete");
  const canApprove = (module: string) => can(module, "approve");

  return {
    role,
    roleName,
    accessRole: normalizedAccessRole,
    permissions,
    isAdmin,
    isPlatformAdmin,
    isManager,
    isViewer,
    can,
    canView,
    canCreate,
    canEdit,
    canDelete,
    canApprove,
  };
};

export default usePermission;
